import {
  errorResponseSchema,
  messageKey,
  refreshResponseSchema,
  type ErrorBody,
} from '@netdeck/contracts';
import type { z } from 'zod';
import { authStore } from './auth-store';

/** База всех маршрутов (ТЗ §8.1). В dev проксируется Vite на api. */
export const API_BASE = '/api/v1';

/**
 * Ошибка запроса к api в терминах контракта §8.1.
 *
 * Несёт машиночитаемый код — интерфейс ветвится по нему, а не по тексту.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown> | undefined;

  constructor(status: number, body: ErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details;
  }
}

/** Сеть недоступна или ответ нечитаем — до кода ошибки дело не дошло. */
export class NetworkError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'NetworkError';
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /**
   * Коды ответа, при которых тело всё равно разбирается схемой, а не считается
   * ошибкой. Нужно там, где не-2xx статус — часть контракта: health отдаёт 503
   * вместе с полноценным описанием того, что именно недоступно.
   */
  acceptStatuses?: readonly number[];
  /** Внутренний признак: этот запрос уже повторяли после обновления токена. */
  retried?: boolean;
  /** Не пытаться обновлять токен — для самих маршрутов аутентификации. */
  skipRefresh?: boolean;
}

/**
 * Обновление access-токена при 401 (§11, фаза 1, задача 10).
 *
 * Промис общий на все параллельные запросы: когда токен истекает, в 401
 * упирается сразу несколько запросов, и каждый не должен запускать свою
 * ротацию — сервер счёл бы это повторным использованием refresh-токена
 * и отозвал бы всё семейство (§10.1).
 */
let refreshInFlight: Promise<boolean> | null = null;

/**
 * Обновление access-токена по требованию.
 *
 * Экспортируется ради тех, кто ходит на сервер не через `apiRequest`:
 * WebSocket предъявляет токен в адресе, и отказ в рукопожатии — его
 * единственный способ узнать, что токен пора обновить. Общий промис
 * обязателен и здесь: две параллельные ротации сервер считает повторным
 * использованием refresh-токена и отзывает всё семейство (§10.1).
 */
export async function refreshAccessToken(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        authStore.clear();
        return false;
      }

      const parsed = refreshResponseSchema.safeParse(await response.json());
      if (!parsed.success) {
        authStore.clear();
        return false;
      }

      authStore.set(parsed.data.accessToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * Запрос к api с разбором ответа Zod-схемой.
 *
 * Валидация ответа обязательна: §6.1 требует проверять все внешние границы,
 * а расхождение контракта фронта и бэка должно всплывать сразу и явно,
 * а не превращаться в `undefined` где-то в глубине дерева компонентов.
 */
export async function apiRequest<T>(
  path: string,
  schema: z.ZodType<T>,
  options: RequestOptions = {},
): Promise<T> {
  const { body, headers, acceptStatuses = [], retried, skipRefresh, ...rest } = options;

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...rest,
      credentials: 'include',
      headers: {
        ...(body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(authStore.token ? { authorization: `Bearer ${authStore.token}` } : {}),
        ...headers,
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch (cause) {
    throw new NetworkError(messageKey('errors.network.unreachable'), { cause });
  }

  // Токен истёк — обновляем и повторяем запрос ровно один раз.
  if (response.status === 401 && !retried && !skipRefresh) {
    if (await refreshAccessToken()) {
      return apiRequest(path, schema, { ...options, retried: true });
    }
  }

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok && !acceptStatuses.includes(response.status)) {
    const parsed = errorResponseSchema.safeParse(payload);
    throw new ApiError(
      response.status,
      parsed.success
        ? parsed.data.error
        : {
            code: 'UNKNOWN',
            message: messageKey('errors.code.byStatus', { status: response.status }),
          },
    );
  }

  const result = schema.safeParse(payload);
  if (!result.success) {
    throw new NetworkError(messageKey('errors.network.badResponse', { path }));
  }

  return result.data;
}
